import { cultivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/CultivoGrpcCatalogService";
import { loteNoProductivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/LoteNoProductivoGrpcCatalogService";
import {
  CultivoItem,
  LoteNoProductivoItem,
} from "../../../../../generated/grpc/catalogos_aplicaciones_pb";
import { LoteAssignEntry, LoteAssignState } from "./LoteAssignState";
import template from "./loteAssignBarTemplate.html";

/** Barra de asignación en bloque del paso 5 (overlay sobre el mapa, mismo
 *  patrón que `AoiSearchBarRender`): muestra la selección actual, deja
 *  elegir "¿Es área productiva?" (Sí/No, `<select>` plano — no hay un
 *  `*Request` único detrás, es una acción en bloque, mismo criterio que los
 *  selects de `AoiSearchBarRender`) y, solo si la finca es de agricultura
 *  (`isAgricultura`, viene de `borrador.tipo === 0`), el cultivo (catálogo
 *  `cultivoGrpcCatalogService`, mismo que usa `LoteEditModal`) —
 *  independiente de si el lote es área productiva: un lote no productivo
 *  puede igual tener cultivo asignado.
 *
 *  "Aplicar" **no guarda nada en el server** — solo actualiza
 *  `LoteAssignState` en memoria (`applyAssignment`), mismo patrón que
 *  el corte del paso 4 (nada se guarda de a poco, se
 *  acumula en el borrador). El guardado real de todo el wizard ocurre una
 *  sola vez, al terminar el paso 6 (todavía sin construir). */
export class LoteAssignBarRender {
  private _state!: LoteAssignState;
  private _esAgricultura = true;
  private _cultivos: CultivoItem[] = [];
  private _coberturas: LoteNoProductivoItem[] = [];

  private _selectionCountEl: HTMLElement | null = null;
  private _selectedTagsEl: HTMLElement | null = null;
  private _selectProductiva: HTMLSelectElement | null = null;
  private _rowCultivo: HTMLElement | null = null;
  private _labelCultivo: HTMLElement | null = null;
  private _selectCultivo: HTMLSelectElement | null = null;
  private _applyBtn: HTMLButtonElement | null = null;
  private _asignadosEl: HTMLElement | null = null;

  render(): string {
    return template;
  }

  /** @param isAgricultura `borrador.tipo === 0`. Decide UNA sola cosa: si al
   *   marcar un lote como productivo se ofrece la lista de CULTIVOS. En
   *   ganadería no (es un potrero, su cobertura es el pasto). Todo lo demás
   *   —incluida la opción "No" y sus coberturas— es igual en los dos tipos.
   *   Ver `_refreshCultivoVisibility`. */
  connect(state: LoteAssignState, isAgricultura: boolean): void {
    this._state = state;
    this._esAgricultura = isAgricultura;

    this._selectionCountEl = document.getElementById("loteAssignSelectionCount");
    this._selectedTagsEl = document.getElementById("loteAssignSelectedTags");
    this._selectProductiva = document.getElementById(
      "loteAssignSelectProductiva",
    ) as HTMLSelectElement;
    this._rowCultivo = document.getElementById("loteAssignRowCultivo");
    this._labelCultivo = document.getElementById("loteAssignLabelCultivo");
    this._asignadosEl = document.getElementById("paso5Asignados");
    this._selectCultivo = document.getElementById(
      "loteAssignSelectCultivo",
    ) as HTMLSelectElement;
    this._applyBtn = document.getElementById(
      "loteAssignApplyBtn",
    ) as HTMLButtonElement;

    // Los DOS catálogos se piden siempre: el de cultivos para los lotes
    // productivos (en cualquier tipo de finca) y el de coberturas para los
    // que no lo son. Cada uno repinta al llegar, porque la respuesta puede
    // caer después de que el usuario ya eligió.
    // Al llegar también se completan los nombres de los lotes que ya venían
    // asignados, para el tooltip del mapa (el borrador solo guarda ids).
    cultivoGrpcCatalogService.getListAsync().then((cultivos) => {
      this._cultivos = cultivos;
      this._refreshCultivoVisibility();
      this._state.resolverNombres(this._cultivos, this._coberturas);
    });
    loteNoProductivoGrpcCatalogService.getListAsync().then((coberturas) => {
      this._coberturas = coberturas;
      this._refreshCultivoVisibility();
      this._state.resolverNombres(this._cultivos, this._coberturas);
    });

    this._selectProductiva?.addEventListener("change", () => {
      // La lista de abajo depende de esta eleccion: cultivos si produce,
      // coberturas si no.
      this._refreshCultivoVisibility();
      this._refreshApplyButton();
    });
    this._selectCultivo?.addEventListener("change", () => this._refreshApplyButton());
    this._applyBtn?.addEventListener("click", this._onApplyClick);

    this._refreshCultivoVisibility();
    this._state.addOnChangeListener(this._refresh);
    this._refresh();
  }

  dispose(): void {}

  private _refresh = (): void => {
    this._refreshSelectedTags();
    this._refreshAsignados();
    this._refreshApplyButton();
  };

  /** Avance de la asignación, que antes vivía en el aside de ayuda del paso 5
   *  (eliminado). Cuenta todos los lotes con asignación, también los
   *  asignados en una visita anterior — ver `LoteAssignState.getTocadosCount`. */
  private _refreshAsignados(): void {
    if (!this._asignadosEl) return;

    const asignados = this._state.getTocadosCount();
    const total = this._state.getTotalCount();
    // Verde solo cuando NO queda ninguno pendiente; naranja mientras falte
    // alguno. Se usan las clases globales de badge, sin colores propios.
    // `total === 0` cae en naranja a propósito: sin lotes no hay nada
    // completo que celebrar.
    const completo = total > 0 && asignados >= total;
    const variante = completo ? "sx-badge-success" : "sx-badge-warning";

    this._asignadosEl.innerHTML =
      `<span class="paso5-asignados-label">Lotes asignados</span>` +
      `<span class="sx-badge ${variante}">${asignados} de ${total}</span>`;
  }

  private _refreshSelectedTags(): void {
    if (!this._selectedTagsEl || !this._selectionCountEl) return;

    const selectedIds = this._state.getSelectedIds();
    this._selectionCountEl.textContent = String(selectedIds.length);

    if (selectedIds.length === 0) {
      this._selectedTagsEl.innerHTML =
        '<span class="lote-assign-bar-tag is-empty">Ninguno seleccionado</span>';
      return;
    }

    const entries = this._state.getAll();
    this._selectedTagsEl.innerHTML = selectedIds
      .map((id) => entries.find((e) => e.id === id))
      .filter((entry): entry is LoteAssignEntry => !!entry)
      .map((entry) => `<span class="lote-assign-bar-tag">${entry.nombreLote}</span>`)
      .join("");
  }

  /** Qué se ofrece en la segunda fila, según "¿Es área productiva?":
   *   - **No** → COBERTURAS (cuerpo de agua, bosque…). Un área que no produce
   *     no tiene cultivo, tiene un uso del suelo. Aplica siempre, también en
   *     fincas de ganadería.
   *   - **Sí** → el catálogo de cultivos, **sólo en agricultura**
   *     (2026-10-09). En ganadería un lote productivo es un potrero y su
   *     cobertura es el pasto: no hay nada que elegir, y ofrecer café o cacao
   *     ahí no tiene sentido — el servidor además los descarta al guardar
   *     (`PersistirFincaAsync`). La fila no se muestra.
   *
   *  **El camino "No" es idéntico en los dos tipos de finca**, y es a
   *  propósito: es como se asignan los bosques, las construcciones y los
   *  cuerpos de agua, que una finca de ganadería tiene igual que una de
   *  agricultura.
   *   - sin elegir todavía → nada.
   *
   *  Antes la fila dependía SOLO del tipo de finca, así que a un lote marcado
   *  como no productivo se le seguía ofreciendo la lista de cultivos. */
  private _refreshCultivoVisibility(): void {
    if (!this._rowCultivo || !this._selectCultivo) return;

    const eleccion = this._selectProductiva?.value ?? "";
    const esCobertura = eleccion === "no";
    // En ganadería un lote productivo es un potrero y su cobertura es el
    // pasto: no hay cultivo que elegir. Los NO productivos sí eligen, igual
    // que en agricultura — es como se asignan bosques, construcciones y
    // cuerpos de agua, que existen en los dos tipos de finca.
    const esCultivo = eleccion === "si" && this._esAgricultura;

    this._rowCultivo.hidden = !esCobertura && !esCultivo;
    if (this._rowCultivo.hidden) {
      this._selectCultivo.value = "";
      return;
    }

    if (this._labelCultivo) {
      this._labelCultivo.textContent = esCobertura ? "Cobertura" : "Cultivo";
    }

    // Los dos vienen del catálogo del server, así que en ambos casos el
    // `value` de la opción es un id real. Antes las coberturas salían de un
    // array fijo en este archivo y su `value` era el propio nombre: no había
    // id que guardar y la elección se perdía al persistir.
    const opciones = esCobertura
      ? this._coberturas.map((c) => ({ valor: c.id, nombre: c.nombre }))
      : this._cultivos.map((c) => ({ valor: c.id, nombre: c.nombre }));

    this._selectCultivo.innerHTML =
      `<option value="">${esCobertura ? "— seleccionar —" : "(Sin cultivo)"}</option>` +
      opciones
        .map((o) => `<option value="${o.valor}">${o.nombre}</option>`)
        .join("");
  }

  private _refreshApplyButton(): void {
    if (!this._applyBtn || !this._selectProductiva) return;

    const hasSelection = this._state.getSelectedIds().length > 0;
    const hasProductivaChoice = this._selectProductiva.value !== "";
    this._applyBtn.disabled = !(hasSelection && hasProductivaChoice);
  }

  private _onApplyClick = (): void => {
    if (!this._selectProductiva) return;

    const isAreaProductiva = this._selectProductiva.value === "si";
    const elegido = this._selectCultivo?.value ?? "";

    // En los dos casos el `value` es el id de catálogo; solo cambia de cuál.
    let catalogoId: string | null = null;
    let catalogoNombre: string | null = null;
    if (elegido) {
      catalogoId = elegido;
      const catalogo = isAreaProductiva ? this._cultivos : this._coberturas;
      catalogoNombre = catalogo.find((c) => c.id === elegido)?.nombre ?? null;
    }
    const ids = this._state.getSelectedIds();
    if (ids.length === 0) return;

    this._state.applyAssignment(ids, isAreaProductiva, catalogoId, catalogoNombre);
    this._selectProductiva.value = "";
    if (this._selectCultivo) this._selectCultivo.value = "";
    this._refreshCultivoVisibility();
  };
}
